import { useEffect, useMemo, useRef, useState } from "react";
import { Clock3, X } from "lucide-react";
import { EMOJI_CATEGORIES, readRecentEmojis, rememberRecentEmoji } from "../emoji";

type Props = Readonly<{
  onClose: () => void;
  onSelect: (emoji: string) => void;
}>;

export function EmojiPicker({ onClose, onSelect }: Props) {
  const [recent, setRecent] = useState(readRecentEmojis);
  const [active, setActive] = useState(() => recent.length > 0 ? "recent" : EMOJI_CATEGORIES[0]!.id);
  const root = useRef<HTMLDivElement>(null);
  const category = useMemo(() => EMOJI_CATEGORIES.find((item) => item.id === active) ?? EMOJI_CATEGORIES[0]!, [active]);
  const emojis = active === "recent" ? recent : category.emojis;
  const label = active === "recent" ? "Недавние" : category.label;

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && root.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[data-emoji-trigger]")) return;
      onClose();
    };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", closeOutside, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  const select = (emoji: string) => {
    setRecent(rememberRecentEmoji(emoji));
    onSelect(emoji);
  };

  return (
    <div aria-label="Смайлики" className="emoji-picker" ref={root} role="dialog">
      <header><b>{label}</b><button aria-label="Закрыть смайлики" className="icon-button" onClick={onClose} type="button"><X /></button></header>
      <div className="emoji-grid" role="list">
        {emojis.map((emoji, index) => <button aria-label={emoji} key={`${emoji}-${index}`} onClick={() => select(emoji)} role="listitem" type="button">{emoji}</button>)}
      </div>
      <nav aria-label="Категории смайликов">
        {recent.length > 0 && <button aria-label="Недавние" className={active === "recent" ? "active" : ""} onClick={() => setActive("recent")} title="Недавние" type="button"><Clock3 /></button>}
        {EMOJI_CATEGORIES.map((item) => <button aria-label={item.label} className={active === item.id ? "active" : ""} key={item.id} onClick={() => setActive(item.id)} title={item.label} type="button">{item.icon}</button>)}
      </nav>
    </div>
  );
}
